import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useQueryMutation } from '@lore/query-connect';
import { DialogManager } from '@/dialogs_new/DialogManager';
import useDialogLauncher from '@/dialogs-routable/_hooks/useDialogLauncher';
import DisclosureFilter from '@/forms/_fields_v2/DisclosureFilter';
import ListItemWithDetail from '@/components/ui/SelectionMultiSelectListItemWithDescription';

export default function SubjectSelector(props) {
  const { user, subjects, userSubjects, defaultOpen } = props;
  const [optimistic, setOptimistic] = useState({});
  const launch = useDialogLauncher();

  const mutationCreate = useQueryMutation('tagNode.create');
  const mutationDestroy = useQueryMutation('tagNode.destroy2');

  function clearOptimistic(subjectId) {
    setOptimistic((prev) => { const next = { ...prev }; delete next[subjectId]; return next; });
  }

  function onToggleSubject(subject) {
    const node = _.find(userSubjects.data, (n) => n._tag.id === subject.id);
    setOptimistic((prev) => ({ ...prev, [subject.id]: !node }));

    if (!node) {
      mutationCreate.mutate(
        { userId: user.id, tagId: subject.id },
        {
          onSuccess: (payload) => userSubjects._add(payload),
          onError: () => clearOptimistic(subject.id),
        }
      );
    } else {
      mutationDestroy.mutate(node._tagNode, {
        onSuccess: (payload) => userSubjects._remove(payload),
        onError: () => clearOptimistic(subject.id),
      });
    }
  }

  function onAddDescription(subject) {
    launch(DialogManager.TagNodeCreate.Config(subject.id, {
      modelName: 'user',
      modelId: user.id,
    }));
  }

  function onEditDescription(node) {
    launch(DialogManager.TagNodeUpdate.Config(node._tagNode.id));
  }

  const selectedCount = userSubjects.data.length;

  return (
    <DisclosureFilter
      label="What subject areas do you teach?"
      count={selectedCount}
      defaultOpen={defaultOpen !== undefined ? defaultOpen : selectedCount > 0}
    >
      <div className="space-y-2">
      {subjects.data.map((subject) => {
        const node = _.find(userSubjects.data, (n) => n._tag.id === subject.id);
        const serverSelected = !!node;
        const selected = subject.id in optimistic ? optimistic[subject.id] : serverSelected;
        const tagNodeDescription = node?._tagNode.data.description;

        return (
          <ListItemWithDetail
            key={subject.id}
            label={subject.data.name}
            description={tagNodeDescription || null}
            selected={selected}
            onChange={() => onToggleSubject(subject)}
            selectedMessage={selected ? (
              tagNodeDescription
                ? 'Update your description.'
                : 'Help others understand what you teach.'
            ) : null}
            actionLabel={selected ? (tagNodeDescription ? 'Edit' : 'Add details') : null}
            onAction={selected ? (
              tagNodeDescription
                ? () => onEditDescription(node)
                : () => onAddDescription(subject)
            ) : null}
          />
        );
      })}
      </div>
    </DisclosureFilter>
  );
}

SubjectSelector.propTypes = {
  user: PropTypes.object.isRequired,
  subjects: PropTypes.object.isRequired,
  userSubjects: PropTypes.object.isRequired,
  defaultOpen: PropTypes.bool,
};
